import React, { useEffect, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { EdiService, formatEdiDate } from '../../../services/billing/edi.service';
import type { EdiCoverageEligibility } from '../../../dtos/billing/edi.dto';
import { usePermission } from '../../../hooks/usePermission';
import { BILLING_WORK, ELIGIBILITY_VIEW, EdiGate, ErrorNote, SimulatedBadge, errorText } from './ediUi';

const RESULT_STYLE: Record<string, string> = {
  ACTIVE: 'text-green-800',
  INACTIVE: 'text-red-700',
  OTHER_PAYER: 'text-amber-900',
};

/** Member id shown as its last four characters only. */
function maskMember(memberId: string): string {
  return memberId.length > 4 ? `••••${memberId.slice(-4)}` : memberId;
}

const EligibilityCardInner: React.FC<{ patientId: string }> = ({ patientId }) => {
  const canCheck = usePermission(BILLING_WORK);
  const [rows, setRows] = useState<EdiCoverageEligibility[] | null>(null);
  const [checking, setChecking] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    EdiService.getEligibility(patientId)
      .then((r) => { if (!cancelled) setRows(r); })
      .catch((err) => { if (!cancelled) setError(errorText(err, 'Failed to load eligibility')); });
    return () => { cancelled = true; };
  }, [patientId]);

  const check = async (coverageId: string) => {
    setChecking(coverageId);
    setError(null);
    try {
      const eligibility = await EdiService.checkEligibility(patientId, coverageId);
      setRows((prev) => prev?.map((r) => (r.coverageId === coverageId ? { ...r, eligibility } : r)) ?? prev);
    } catch (err) {
      setError(errorText(err, 'The eligibility check failed'));
    } finally {
      setChecking(null);
    }
  };

  if (!rows && !error) return null;

  return (
    <section aria-label="Eligibility" className="mt-3 rounded-lg border border-slate-200 bg-white p-3 text-sm">
      <h3 className="mb-2 flex items-center gap-1.5 font-semibold text-slate-800"><ShieldCheck size={16} /> Eligibility</h3>
      <ErrorNote message={error} />
      {rows && rows.length === 0 && <p className="text-slate-500">No coverage to check.</p>}
      <ul className="divide-y divide-slate-100">
        {(rows || []).map((r) => (
          <li key={r.coverageId} className="flex flex-col gap-1 py-2 sm:flex-row sm:items-center sm:justify-between" data-testid={`elig-${r.coverageId}`}>
            <div className="min-w-0">
              <p className="font-medium text-slate-800">{r.payerName}{r.memberId ? ` · ${maskMember(r.memberId)}` : ''}</p>
              {r.eligibility ? (
                <p className="text-xs text-slate-600">
                  <span className={`font-medium ${RESULT_STYLE[r.eligibility.status] || 'text-slate-700'}`}>{r.eligibility.status.replace(/_/g, ' ').toLowerCase()}</span>
                  {' · '}checked {formatEdiDate(r.eligibility.checkedAt)} · {r.eligibility.source}
                  {r.eligibility.simulated && <SimulatedBadge className="ml-1" />}
                  {r.eligibility.message && <span className="block break-words">{r.eligibility.message}</span>}
                </p>
              ) : (
                <p className="text-xs text-slate-500">Not checked yet</p>
              )}
            </div>
            {canCheck && (
            <button
              type="button"
              disabled={checking !== null}
              onClick={() => check(r.coverageId)}
              className="self-start rounded-md border border-slate-300 px-3 py-1 text-sm text-slate-700 disabled:opacity-50 sm:self-auto"
            >
              {checking === r.coverageId ? 'Checking…' : 'Check now'}
            </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};

/** Shown only to claims readers and Pelora Billing roles; no request otherwise. */
const EligibilityCardGated: React.FC<{ patientId: string }> = ({ patientId }) => {
  const canView = usePermission(ELIGIBILITY_VIEW);
  return canView ? <EligibilityCardInner patientId={patientId} /> : null;
};

/** Eligibility card under a patient's coverage list. Nothing renders (and no request) when billingEdi is off. */
const EligibilityCard: React.FC<{ patientId: string }> = ({ patientId }) => (
  <EdiGate>
    <EligibilityCardGated patientId={patientId} />
  </EdiGate>
);

export default EligibilityCard;
